<script setup lang="ts">
/***
 * template to show array of enums
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { getStatusTagSeverity } from '@/utils/priority';
import { ContractActionV1Dto } from '@/openapi/models/ContractActionV1Dto';

import type { DataTableCellProps } from './templateTypes';
const { t } = useI18n();
const props = defineProps<DataTableCellProps>();

const availableItems = computed(() =>
  Array.isArray(props.cell.value)
    ? props.cell.value.filter(
        (item: { status: string }) => item.status?.toLowerCase() === 'available',
      )
    : [],
);

const activeItems = computed(() =>
  Array.isArray(props.cell.value)
    ? props.cell.value.filter((item: { status: string }) => item.status?.toLowerCase() === 'active')
    : [],
);
</script>

<template>
  <ExpandableCell :data-key="dataKey" :field="column.field">
    <!-- Collapsed content -->
    <template #collapsed>
      <div class="flex gap-2">
        <div class="flex-1 flex items-center gap-2">
          <Tag
            v-if="activeItems?.length"
            :value="
              activeItems?.length +
              ' ' +
              t(`enums.${ContractActionV1Dto.status.ACTIVE.toUpperCase()}`)
            "
            :severity="
              getStatusTagSeverity(ContractActionV1Dto.status.ACTIVE as ContractActionV1Dto.status)
            "
          />
          <Tag
            v-if="availableItems?.length"
            :value="
              availableItems?.length +
              ' ' +
              t(`enums.${ContractActionV1Dto.status.AVAILABLE.toUpperCase()}`)
            "
            :severity="
              getStatusTagSeverity(
                ContractActionV1Dto.status.AVAILABLE as ContractActionV1Dto.status,
              )
            "
          />
          [...]
        </div>
      </div>
    </template>

    <!-- Expanded content -->
    <template #expanded>
      <div v-for="(arrayValue, arrayIndex) in cell.value" :key="arrayIndex" class="py-1">
        <span class="nameValue"
          >{{ t(`labels.type`) }}: {{ t(`enums.${arrayValue.type.toUpperCase()}`) }}</span
        >
        <span class="nameValue"
          >{{ t(`labels.actingBy`) }}: {{ t(`enums.${arrayValue.actingBy.toUpperCase()}`) }}</span
        >
        <span class="nameValue">
          {{ t(`labels.status`) }}:
          <Tag
            :value="t(`enums.${arrayValue.status.toUpperCase()}`)"
            :severity="getStatusTagSeverity(arrayValue.status)"
          />
        </span>
      </div>
    </template>
  </ExpandableCell>
</template>

<style scoped lang="css">
@layer app {
  .nameValue {
    display: block;
    white-space: nowrap;
  }
}
</style>
